import React from 'react';

import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { DatasourcesDisplayGridRowDataFragment, PublishedDatasourceOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GenerateCertificationTooltipComponent,
  getDSHoverTitleString,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  TableauUser,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

const shouldBeObfuscated: (rowData: DatasourcesDisplayGridRowData) => boolean = ({ name }) => name === null;
const projectContainerType = 'project';

export interface DatasourcesDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly description: string;
  readonly vizportalUrlId: string;
  readonly workbookCount: number | null;
  readonly owner: TableauUser;
  readonly hasExtracts: boolean;
  readonly isCertified: boolean;
  readonly extractLastUpdateTime: string | null;
  readonly projectVizportalUrlId: string | null;
  readonly containerName: string | null;
  readonly containerType: string;
  readonly certifier: TableauUser | null;
  readonly certificationNote: string;
}

export function getRowDataForDatasource(datasource: DatasourcesDisplayGridRowDataFragment): DatasourcesDisplayGridRowData {
  return {
    id: datasource.id,
    name: datasource.name,
    description: datasource.description || '',
    owner: datasource.owner,
    workbookCount: datasource.downstreamWorkbooksConnection ? datasource.downstreamWorkbooksConnection.totalCount : null,
    vizportalUrlId: datasource.vizportalUrlId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: datasource.extractLastUpdateTime || null,
    projectVizportalUrlId: datasource.projectVizportalUrlId,
    containerName: datasource.containerName,
    containerType: datasource.containerType,
    hasExtracts: !!datasource.hasExtracts,
    isCertified: datasource.isCertified,
    certifier: datasource.certifier,
    certificationNote: datasource.certificationNote || '',
  };
}

type DatasourcesColumnFactory = ColumnFactory<
  GridSortProps<DatasourcesValidSortFields>,
  DatasourcesDisplayGridRowData,
  DatasourcesValidSortFields
>;

function columns(): ReadonlyArray<DatasourcesColumnFactory> {
  const cols: ReadonlyArray<DatasourcesColumnFactory> = [
    (sortParams) => ({
      name: 'type',
      dataKey: PublishedDatasourceOrderField.IS_CERTIFIED,
      cellFormatter: createIconCellFormatter(
        ({ isCertified, hasExtracts, certifier, certificationNote, extractLastUpdateTime }) =>
          GenerateCertificationTooltipComponent(isCertified, extractLastUpdateTime, hasExtracts, certifier, certificationNote),
        ({ isCertified, hasExtracts }) => getDSHoverTitleString(isCertified, hasExtracts)
      ),
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'name',
      dataKey: PublishedDatasourceOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.datasourceLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'downstreamWorkbooks',
      dataKey: PublishedDatasourceOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderConnectionCountWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter<DatasourcesDisplayGridRowData>(({ workbookCount }) => workbookCount),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'extractLastUpdateTime',
      dataKey: UnsortableColumnKey,
      cellFormatter: createTextCellFormatter(({ hasExtracts, extractLastUpdateTime }) =>
        !hasExtracts
          ? Messages.LiveConnection()
          : extractLastUpdateTime !== null
            ? Formatters.formatMediumDateTimeWithoutSeconds(new Date(extractLastUpdateTime))
            : ''
      ),
      headerText: Messages.GridHeaderLastExtract(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'location',
      dataKey:
        PublishedDatasourceOrderField.CONTAINER_NAME,
      headerText: Messages.GridHeaderLocation(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<DatasourcesColumnFactory>;
}

export type DatasourcesDisplayGridProps = UsualDisplayGridProps<DatasourcesDisplayGridRowData, GraphQLError> &
  GridSortProps<DatasourcesValidSortFields>;

export function DatasourcesDisplayGrid(props: DatasourcesDisplayGridProps): JSX.Element {
  const cols = columns().map((columnFactory) => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
